import { useRef } from 'react'

import { Form } from 'factorial-form'
import {
  EmployeeBlocksEmployeeFragment,
  EmployeeBlocksEmployeeFragmentDoc,
  EmployeeBlocksUpdateCommunicationsEmailDocument,
  EmployeeBlocksUpdateGeneralInformationDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormComponent from 'modules/core/components/Form'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { stringToDate } from 'modules/core/lib/dates'
import { formGetDirtyFields } from 'modules/core/lib/formGetDirtyFields'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import Fields from './Fields'

interface Props {
  employee: EmployeeBlocksEmployeeFragment
  readonly?: boolean
}

export class GeneralForm extends Form {
  constructor(values: Record<string, unknown>) {
    super(values, {
      birthday_on: 'date',
      birth_name: 'string',
      birthplace: 'string',
      country_of_birth: 'string',
      communications_email: 'string',
      first_name: 'string',
      gender: 'string',
      last_name: 'string',
      nationality: 'string',
      phone_number: 'string',
      preferred_name: 'string',
      pronouns: 'string',
      share_birthday: 'boolean',
      unconfirmed_communications_email: 'string',
    })
  }
}

const GeneralBlock = ({ employee, readonly }: Props) => {
  const form = useRef(
    new GeneralForm({
      birthday_on: employee.birthdayOn,
      birth_name: employee.birthName,
      birthplace: employee.birthplace,
      country_of_birth: employee.countryOfBirth,
      communications_email: employee.communicationsEmail,
      first_name: employee.firstName,
      gender: employee.gender,
      last_name: employee.lastName,
      nationality: employee.nationality,
      phone_number: employee.phoneNumber,
      preferred_name: employee.preferredName,
      pronouns: employee.pronouns,
      share_birthday: employee.shareBirthday,
      unconfirmed_communications_email: employee.unconfirmedCommunicationsEmail,
    })
  )
  const generalForm = form.current

  const [updateIdentificationData] = useMutation(EmployeeBlocksUpdateGeneralInformationDocument, {
    extractorFn: (data) => data.employeesMutations.updateEmployeesEmployee,
    update: (cache, result) => {
      const updatedEmployee = result.data?.employeesMutations?.updateEmployeesEmployee?.employee
      if (updatedEmployee) {
        cache.writeFragment({
          id: cache.identify(employee),
          fragment: EmployeeBlocksEmployeeFragmentDoc,
          fragmentName: 'EmployeeBlocksEmployee',
          data: {
            ...employee,
            birthdayOn: updatedEmployee.birthdayOn,
            birthName: updatedEmployee.birthName,
            birthplace: updatedEmployee.birthplace,
            countryOfBirth: updatedEmployee.countryOfBirth,
            firstName: updatedEmployee.firstName,
            gender: updatedEmployee.gender,
            lastName: updatedEmployee.lastName,
            nationality: updatedEmployee.nationality,
            phoneNumber: updatedEmployee.phoneNumber,
            preferredName: updatedEmployee.preferredName,
            pronouns: updatedEmployee.pronouns,
            shareBirthday: updatedEmployee.shareBirthday,
          },
        })
      }
    },
  })

  const [updateCommunicationsEmail] = useMutation(EmployeeBlocksUpdateCommunicationsEmailDocument, {
    extractorFn: (data) => data.usersMutations.updateUsersPersonalInfo,
    update: (cache, result) => {
      const personalInfo = result.data?.usersMutations?.updateUsersPersonalInfo?.personalInfo
      if (personalInfo) {
        cache.writeFragment({
          id: cache.identify(employee),
          fragment: EmployeeBlocksEmployeeFragmentDoc,
          fragmentName: 'EmployeeBlocksEmployee',
          data: {
            ...employee,
            communicationsEmail: personalInfo.communicationsEmail,
            unconfirmedCommunicationsEmail: personalInfo.unconfirmedCommunicationsEmail,
          },
        })
      }
    },
  })

  const trackEvent = () => {
    track(trackingEvents.employee.editEmployeeInformationSubmit, {
      fields_edited: [...formGetDirtyFields(generalForm)],
      legal_entity_country: employee.legalEntity?.country,
      layout_version: 'v1',
    })
  }

  const onUpdateCommunicationsEmail = async () => {
    if (!generalForm.get('communications_email').isDirty) {
      // If the communications email is not changed we save the call
      return Promise.resolve()
    }

    const result = await updateCommunicationsEmail({
      variables: {
        accessId: employee.access.id,
        communicationsEmail: generalForm.get('communications_email').value,
      },
    })

    let formErrors: Record<string, string> = {}
    const handledResult = handleResult(result, (errors) => {
      if (errors) {
        formErrors = errors
      }
    })

    if (!handledResult.success) {
      const errorKeys = Object.keys(formErrors)
      const message =
        errorKeys.length > 0
          ? `${i18n.t('profile.employee.general.update_communications_email_error')} (${errorKeys
              .map((key) => {
                return formErrors[key]
              })
              .join(', ')})`
          : i18n.t('profile.employee.general.update_communications_email_error')

      return Promise.reject(new Error(message))
    }

    return result
  }

  const onUpdateIdentificationData = async () => {
    const dirtyFields = formGetDirtyFields(generalForm)

    // Fields that are passed to the mutation variables
    const mutationFields = [
      'birthday_on',
      'birth_name',
      'birthplace',
      'country_of_birth',
      'first_name',
      'gender',
      'last_name',
      'nationality',
      'phone_number',
      'preferred_name',
      'pronouns',
      'share_birthday',
    ]

    // Check if any of the mutation fields are dirty
    const hasRelevantDirtyFields = mutationFields.some((field) => dirtyFields.includes(field))
    if (!hasRelevantDirtyFields) {
      // If none of the mutation fields are dirty, we save the call
      return Promise.resolve()
    }

    const birthdayOnValue = generalForm.get('birthday_on').value
    const birthdayOn = birthdayOnValue
      ? (() => {
          const date = stringToDate(birthdayOnValue, 'ui')
          return new Date(Date.UTC(date.year(), date.month(), date.date())).toISOString()
        })()
      : undefined
    const result = await updateIdentificationData({
      variables: {
        employeeId: employee.id,
        birthdayOn: birthdayOn,
        birthName: generalForm.get('birth_name').value,
        birthplace: generalForm.get('birthplace').value,
        countryOfBirth: generalForm.get('country_of_birth').value,
        firstName: generalForm.get('first_name').value,
        gender: generalForm.get('gender').value,
        lastName: generalForm.get('last_name').value,
        nationality: generalForm.get('nationality').value,
        phoneNumber: generalForm.get('phone_number').value,
        preferredName: generalForm.get('preferred_name').value,
        pronouns: generalForm.get('pronouns').value,
        shareBirthday: generalForm.get('share_birthday').value,
      },
    })

    let formErrors: Record<string, string> = {}
    const handledResult = handleResult(result, (errors) => {
      if (errors) {
        formErrors = errors
      }
    })

    if (!handledResult.success) {
      const errorKeys = Object.keys(formErrors)
      const message =
        errorKeys.length > 0
          ? `${i18n.t('profile.employee.general.update_general_data_error')} (${errorKeys
              .map((key) => {
                return formErrors[key]
              })
              .join(', ')})`
          : i18n.t('profile.employee.general.update_general_data_error')

      return Promise.reject(new Error(message))
    }

    return result
  }

  const onSubmit = async () => {
    trackEvent()

    onUpdateCommunicationsEmail()
      .then(() => {
        onUpdateIdentificationData()
          .then(() => {
            generalForm.cleanAll()
            app.setFlash({
              type: 'correct',
              message: i18n.t('profile.employee.general.update_general_data'),
            })
          })
          .catch((error) => {
            app.setFlash({
              type: 'wrong',
              message: error.message,
            })
          })
      })
      .catch((error) => {
        app.setFlash({
          type: 'wrong',
          message: error.message,
        })
      })
  }

  return (
    <FormComponent onSubmit={onSubmit}>
      <Fields employee={employee} form={generalForm} readonly={readonly} />
    </FormComponent>
  )
}

export default observer(GeneralBlock)
